'use client';

import { useIsLargeTablet } from '@workspace/ui/hooks';
import type { MenuItem, NotificationCategory, NotificationSubcategory } from '@workspace/ui/types';
import {
  Banknote,
  BriefcaseBusiness,
  Calendar,
  CalendarPlus,
  ChartColumn,
  CircleHelp,
  CreditCard,
  FileClock,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  ReceiptText,
  Settings,
  Users,
  WalletCards,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_SUBCATEGORIES } from '../../lib/constants';
import type { NavNotificationCounts } from '../../lib/app-navigation-notifications';
import { ThemeToggle } from '../custom/menu/theme-toggle';
import { AppNavbar } from './app-navbar';
import { AppSidebar } from './app-sidebar';
import { HELP_DESK_URL } from './app-top-header-help-desk';

export const APP_WALLET = 'wallet' as const;
export const APP_EOR = 'eor' as const;
export type AppNav = typeof APP_WALLET | typeof APP_EOR;

/** Menu titles are hardcoded English labels shared by EOR and Wallet. */

function getNavNotificationCount(
  params: {
    category?: NotificationCategory;
    subcategory?: NotificationSubcategory;
  },
  notificationCounts?: NavNotificationCounts
): number {
  if (!notificationCounts) return 0;
  if (params.subcategory) {
    return notificationCounts.bySubcategory[params.subcategory] ?? 0;
  }
  if (params.category) {
    return notificationCounts.byCategory[params.category] ?? 0;
  }
  return 0;
}

function resolveMonthlyInputsNavUrl(params: {
  featureFlags: Record<string, boolean>;
  isPartnerOrganisation: boolean;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): string {
  const { isPartnerOrganisation, getLinkUrl } = params;
  const path = isPartnerOrganisation ? '/monthly-inputs-partner' : '/monthly-inputs';
  return getLinkUrl(path, APP_EOR);
}

function resolvePayrollToRunNavUrl(params: {
  isGlobalAdmin?: boolean;
  isEmployer?: boolean;
  canViewPayrollToRun?: boolean;
  featureFlags: Record<string, boolean>;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): string | undefined {
  const { isGlobalAdmin, isEmployer, canViewPayrollToRun, featureFlags, getLinkUrl } = params;

  if (!featureFlags['show-payroll-to-run']) return undefined;
  if (!canViewPayrollToRun) return undefined;

  if (isGlobalAdmin) return getLinkUrl('/payroll-to-run', APP_EOR);
  if (!isEmployer) return undefined;
  return getLinkUrl('/payroll-to-run', APP_EOR);
}

function applyNotificationBadgesToItems(
  items: MenuItem[],
  notificationCounts?: NavNotificationCounts
): MenuItem[] {
  return items.map((item) => {
    if (item.items?.length) {
      const subitemsWithBadge = item.items.map((sub) => {
        const count = getNavNotificationCount(
          { category: sub.category, subcategory: sub.subcategory },
          notificationCounts
        );
        return { ...sub, ...(count > 0 ? { badge: count } : {}) };
      });
      const parentCount = item.category
        ? getNavNotificationCount({ category: item.category }, notificationCounts)
        : subitemsWithBadge.reduce((sum, sub) => sum + (sub.badge ?? 0), 0);
      return {
        ...item,
        items: subitemsWithBadge,
        ...(parentCount > 0 ? { badge: parentCount } : {}),
      };
    }
    const count = getNavNotificationCount(
      { category: item.category, subcategory: item.subcategory },
      notificationCounts
    );
    return { ...item, ...(count > 0 ? { badge: count } : {}) };
  });
}

function payrollNavGroup(params: {
  app: AppNav;
  monthlyInputsUrl: string;
  payrollUrl: string;
  /** When set (employer / contractor), inserts Payroll to run after EOR payroll. */
  payrollToRunUrl?: string;
  /** When false, omit EOR payroll (e.g. contractor team lead). Default true. */
  includeEorPayroll?: boolean;
}): MenuItem {
  const { monthlyInputsUrl, payrollUrl, payrollToRunUrl, includeEorPayroll = true } = params;

  const subitems = [
    {
      title: 'Monthly inputs',
      url: monthlyInputsUrl,
      subcategory: NOTIFICATION_SUBCATEGORIES.MONTHLY_INPUTS as NotificationSubcategory,
    },
    ...(includeEorPayroll
      ? [
          {
            title: 'EOR Payroll',
            url: payrollUrl,
            subcategory: NOTIFICATION_SUBCATEGORIES.EOR_PAYROLL as NotificationSubcategory,
          },
        ]
      : []),
    ...(payrollToRunUrl
      ? [
          {
            title: 'Payroll to run',
            url: payrollToRunUrl,
            subcategory: NOTIFICATION_SUBCATEGORIES.PAYROLL_TO_RUN as NotificationSubcategory,
          },
        ]
      : []),
  ];

  if (subitems.length === 1) {
    const onlySubitem = subitems[0]!;
    return {
      title: onlySubitem.title,
      url: onlySubitem.url,
      icon: <Banknote />,
      urlPrefix: '/monthly-inputs',
      ...(onlySubitem.subcategory ? { subcategory: onlySubitem.subcategory } : {}),
    };
  }

  return {
    title: 'Payroll',
    url: monthlyInputsUrl,
    icon: <Banknote />,
    urlPrefix: '/monthly-inputs',
    category: NOTIFICATION_CATEGORIES.PAYROLL as NotificationCategory,
    items: subitems,
  };
}

function billingNavItem(
  billingUrl: string,
  category: NotificationCategory = NOTIFICATION_CATEGORIES.INVOICES
): MenuItem {
  return {
    title: 'Billing',
    url: billingUrl,
    icon: <FileSpreadsheet />,
    category,
  };
}

function paymentsNavGroup(params: {
  clientInvoicingUrl: string;
  recipientsUrl: string;
  contractorPaymentsUrl?: string;
}): MenuItem {
  const { clientInvoicingUrl, recipientsUrl, contractorPaymentsUrl } = params;

  return {
    title: 'Payments',
    url: clientInvoicingUrl,
    icon: <CreditCard />,
    items: [
      {
        title: 'Client invoicing',
        url: clientInvoicingUrl,
      },
      ...(contractorPaymentsUrl
        ? [
            {
              title: 'Contractors',
              url: contractorPaymentsUrl,
            },
          ]
        : []),
      {
        title: 'Recipients',
        url: recipientsUrl,
      },
    ],
  };
}

function peopleNavGroup(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  hasEmployerRole: boolean;
  isGlobalAdmin: boolean;
}): MenuItem {
  const overviewUrl = params.getLinkUrl('/people/overview', APP_EOR);
  const teamsManagementUrl = params.getLinkUrl('/people/teams-management', APP_EOR);
  const urlPrefix = params.app === APP_EOR ? { urlPrefix: '/people' as const } : {};
  const canViewTeamsManagement = params.hasEmployerRole || params.isGlobalAdmin;

  return {
    title: 'People',
    url: overviewUrl,
    icon: <Users />,
    category: NOTIFICATION_CATEGORIES.TEAM_MEMBERS,
    ...urlPrefix,
    items: [
      {
        title: 'People overview',
        url: overviewUrl,
      },
      ...(canViewTeamsManagement
        ? [
            {
              title: 'Team management',
              url: teamsManagementUrl,
            },
          ]
        : []),
    ],
  };
}

function payslipsNavItem(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): MenuItem {
  const payslipsUrl = params.getLinkUrl('/payslips', APP_EOR);
  return {
    title: 'Payslips',
    url: payslipsUrl,
    ...(params.app === APP_EOR ? { urlPrefix: '/payslips' } : {}),
    icon: <FileText />,
    category: NOTIFICATION_CATEGORIES.PAYSLIP,
  };
}

function reportsNavItems(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  enabled: boolean;
}): MenuItem[] {
  if (!params.enabled) return [];
  return [
    {
      title: 'Reports',
      url: params.getLinkUrl('/reports', APP_EOR),
      icon: <ChartColumn />,
    },
  ];
}

function absencesNavItems(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  enabled: boolean;
}): MenuItem[] {
  if (!params.enabled) return [];
  const absencesUrl = params.getLinkUrl('/absences', APP_EOR);
  return [
    {
      title: 'Absences',
      url: absencesUrl,
      ...(params.app === APP_EOR ? { urlPrefix: '/absences' } : {}),
      icon: <Calendar />,
      category: NOTIFICATION_CATEGORIES.ABSENCE,
    },
  ];
}

/** Payroll / monthly-inputs rows and optional billing (split so main nav can match global order: … Payroll, Reports, Wallet, Billing …). */
function getEmployedEmployeePayrollAndBilling(params: {
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  monthlyInputsUrl: string;
  payrollUrl: string;
  billingUrl: string;
  featureFlags: Record<string, boolean>;
  canViewPayrollToRun?: boolean;
}): { payrollItems: MenuItem[]; billingItem: MenuItem | null } {
  const {
    isGlobalAdmin,
    isTeamLead,
    app,
    getLinkUrl,
    monthlyInputsUrl,
    payrollUrl,
    billingUrl,
    featureFlags,
    canViewPayrollToRun,
  } = params;

  const payrollToRunUrl = resolvePayrollToRunNavUrl({
    isGlobalAdmin,
    canViewPayrollToRun,
    featureFlags,
    getLinkUrl,
  });

  if (isGlobalAdmin) {
    return {
      payrollItems: [
        payrollNavGroup({
          app,
          monthlyInputsUrl,
          payrollUrl,
          payrollToRunUrl,
        }),
      ],
      billingItem: billingNavItem(billingUrl),
    };
  }

  if (isTeamLead) {
    return {
      payrollItems: [
        payrollNavGroup({
          app,
          monthlyInputsUrl,
          payrollUrl,
          includeEorPayroll: false,
        }),
      ],
      billingItem: null,
    };
  }

  // Non–global-admin employees: monthly inputs only (not /payroll), on every breakpoint.
  return {
    payrollItems: [
      {
        title: 'Monthly inputs',
        url: monthlyInputsUrl,
        icon: <CalendarPlus />,
        urlPrefix: '/monthly-inputs',
        subcategory: NOTIFICATION_SUBCATEGORIES.MONTHLY_INPUTS as NotificationSubcategory,
      },
    ],
    billingItem: null,
  };
}

function buildEmployerMainItems(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  hasEmployerRole: boolean;
  isGlobalAdmin: boolean;
  featureFlags: Record<string, boolean>;
  canViewPayrollToRun?: boolean;
  isPartnerOrganisation: boolean;
  walletLink: MenuItem;
  expensesLink: MenuItem;
  contractorPaymentsLink: MenuItem;
}): MenuItem[] {
  const {
    app,
    getLinkUrl,
    hasEmployerRole,
    isGlobalAdmin,
    featureFlags,
    isPartnerOrganisation,
    canViewPayrollToRun,
    walletLink,
    expensesLink,
    contractorPaymentsLink,
  } = params;
  const dashboardUrl = getLinkUrl('/', APP_EOR);
  const monthlyInputsUrl = resolveMonthlyInputsNavUrl({
    featureFlags,
    isPartnerOrganisation,
    getLinkUrl,
  });
  const payrollToRunUrl = resolvePayrollToRunNavUrl({
    isEmployer: true,
    canViewPayrollToRun,
    featureFlags,
    getLinkUrl,
  });
  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);
  const recipientsUrl = getLinkUrl('/payment-recipients', APP_WALLET);
  const clientInvoicingUrl = getLinkUrl('/client-invoicing', APP_WALLET);
  const contractorPaymentsUrl = featureFlags?.['show-contractor-payments']
    ? contractorPaymentsLink.url
    : undefined;

  return [
    {
      title: 'Dashboard',
      url: dashboardUrl,
      icon: <LayoutGrid />,
    },
    peopleNavGroup({
      app,
      getLinkUrl,
      hasEmployerRole,
      isGlobalAdmin,
    }),
    payrollNavGroup({
      app,
      monthlyInputsUrl,
      payrollUrl,
      payrollToRunUrl,
    }),
    ...absencesNavItems({
      app,
      getLinkUrl,
      enabled: true,
    }),
    expensesLink,
    billingNavItem(billingUrl),
    walletLink,
    paymentsNavGroup({
      clientInvoicingUrl,
      contractorPaymentsUrl,
      recipientsUrl,
    }),
    ...reportsNavItems({
      app,
      getLinkUrl,
      enabled: true,
    }),
  ];
}

function buildContractorMainItems(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  canViewPayrollToRun?: boolean;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
  expensesLink: MenuItem;
  contractorPaymentsLink: MenuItem;
  hasEmployerRole: boolean;
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
}): MenuItem[] {
  const {
    app,
    getLinkUrl,
    featureFlags,
    canViewPayrollToRun,
    walletLink,
    clientInvoicingLink,
    expensesLink,
    contractorPaymentsLink,
    hasEmployerRole,
    isGlobalAdmin,
    isTeamLead,
  } = params;

  const monthlyInputsUrl = getLinkUrl('/monthly-inputs', APP_EOR);

  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);

  const teamLeadPayrollItems: MenuItem[] =
    isTeamLead && !isGlobalAdmin
      ? [
          payrollNavGroup({
            app,
            monthlyInputsUrl,
            payrollUrl,
            includeEorPayroll: false,
          }),
        ]
      : [];

  const contractorPaymentsIfEnabled = featureFlags?.['show-contractor-payments']
    ? [contractorPaymentsLink]
    : [];

  const payrollToRunUrl = resolvePayrollToRunNavUrl({
    isGlobalAdmin,
    canViewPayrollToRun,
    featureFlags,
    getLinkUrl,
  });

  if (isGlobalAdmin) {
    return [
      peopleNavGroup({ app, getLinkUrl, hasEmployerRole, isGlobalAdmin }),
      ...absencesNavItems({
        app,
        getLinkUrl,
        enabled: true,
      }),
      expensesLink,
      payrollNavGroup({
        app,
        monthlyInputsUrl,
        payrollUrl,
        payrollToRunUrl,
      }),
      ...reportsNavItems({
        app,
        getLinkUrl,
        enabled: true,
      }),
      walletLink,
      billingNavItem(billingUrl),
      clientInvoicingLink,
      ...contractorPaymentsIfEnabled,
    ];
  }

  return [
    walletLink,
    expensesLink,
    clientInvoicingLink,
    ...teamLeadPayrollItems,
    ...contractorPaymentsIfEnabled,
  ];
}

function buildGigWorkerMainItems(params: {
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { walletLink, clientInvoicingLink } = params;

  return [walletLink, clientInvoicingLink];
}

function buildB2CStandardMainItems(params: {
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { walletLink, clientInvoicingLink } = params;

  return [walletLink, clientInvoicingLink];
}

function buildB2CEmployeeMainItems(params: {
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { getLinkUrl, walletLink, clientInvoicingLink } = params;

  const employmentUrl = getLinkUrl('/employment', APP_EOR);

  return [
    // TODO - Dashboard to be added here
    // TODO - Monthly inputs to be added here
    walletLink,
    {
      title: 'Employment',
      url: employmentUrl,
      icon: <BriefcaseBusiness />,
    },
    clientInvoicingLink,
  ];
}

function buildEmployeeMainItems(params: {
  app: AppNav;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  canViewPayrollToRun?: boolean;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
  expensesLink: MenuItem;
  hasEmployerRole: boolean;
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
  isEmployed: boolean;
}): MenuItem[] {
  const {
    app,
    getLinkUrl,
    featureFlags,
    canViewPayrollToRun,
    walletLink,
    clientInvoicingLink,
    expensesLink,
    hasEmployerRole,
    isGlobalAdmin,
    isTeamLead,
    isEmployed,
  } = params;

  const dashboardUrl = getLinkUrl('/', APP_EOR);
  const employmentUrl = getLinkUrl('/employment', APP_EOR);
  const monthlyInputsUrl = getLinkUrl('/monthly-inputs', APP_EOR);
  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);
  const { payrollItems, billingItem } = isEmployed
    ? getEmployedEmployeePayrollAndBilling({
        featureFlags,
        isGlobalAdmin,
        isTeamLead,
        app,
        getLinkUrl,
        monthlyInputsUrl,
        payrollUrl,
        billingUrl,
        canViewPayrollToRun,
      })
    : { payrollItems: [] as MenuItem[], billingItem: null };

  return [
    {
      title: 'Dashboard',
      url: dashboardUrl,
      icon: <LayoutGrid />,
    },
    ...(isGlobalAdmin ? [peopleNavGroup({ app, getLinkUrl, hasEmployerRole, isGlobalAdmin })] : []),
    ...absencesNavItems({
      app,
      getLinkUrl,
      enabled: isEmployed || isGlobalAdmin,
    }),
    expensesLink,
    ...payrollItems,
    ...(isGlobalAdmin
      ? reportsNavItems({
          app,
          getLinkUrl,
          enabled: true,
        })
      : []),
    walletLink,
    ...(billingItem ? [billingItem] : []),
    {
      title: 'Employment',
      url: employmentUrl,
      icon: <BriefcaseBusiness />,
    },
    clientInvoicingLink,
  ];
}

function buildFooterMenuItems(params: {
  isMobile: boolean;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  helpDeskLink: MenuItem;
  showNotifications: boolean;
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (open: boolean) => void;
  renderNotificationsMobile: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  slotNotifications: ReactNode;
  slotLogout: ReactNode;
  showHelpDeskInSidebar: boolean;
}): MenuItem[] {
  const {
    isMobile,
    getLinkUrl,
    helpDeskLink,
    showNotifications,
    isNotificationsOpen,
    setIsNotificationsOpen,
    renderNotificationsMobile,
    slotNotifications,
    slotLogout,
    showHelpDeskInSidebar,
  } = params;

  const settingsUrl = getLinkUrl('/settings', APP_EOR);

  const settingsItem: MenuItem = {
    title: 'Settings',
    url: settingsUrl,
    icon: <Settings />,
    category: NOTIFICATION_CATEGORIES.SETTINGS,
    ...(isMobile ? { spaceAbove: true } : { bottom: true }),
  };

  const notificationsItem: MenuItem = {
    title: 'Notifications',
    component: isMobile
      ? renderNotificationsMobile({
          isOpen: isNotificationsOpen,
          setIsOpen: setIsNotificationsOpen,
        })
      : slotNotifications,
    ...(isMobile ? {} : { bottom: true }),
  };

  const themeItem: MenuItem = {
    title: 'Theme',
    component: <ThemeToggle />,
    ...(isMobile ? {} : { bottom: true }),
  };

  const logoutItem: MenuItem = {
    title: 'Logout',
    component: slotLogout,
    bottomSeparated: true,
  };

  const helpDeskFooterItem: MenuItem = {
    ...helpDeskLink,
    ...(isMobile ? {} : { bottom: true }),
  };

  return [
    settingsItem,
    ...(showNotifications ? [notificationsItem] : []),
    themeItem,
    ...(showHelpDeskInSidebar ? [helpDeskFooterItem] : []),
    logoutItem,
  ];
}

export interface AppNavigationProps {
  /** Which app is hosting this navigation (determines base URLs for current vs external app). */
  app: AppNav;
  isEmployer: boolean;
  isContractor: boolean;
  isGigWorker: boolean;
  isB2CStandard: boolean;
  isB2CFree: boolean;
  isB2CEmployee: boolean;
  isEmployed: boolean;
  /** Whether the user is a team lead. Team leads see a restricted set of nav items. */
  isTeamLead?: boolean;
  /** Whether the user is a team member. Team members see a fixed set of nav items. */
  isTeamMember?: boolean;
  /** Whether the user is a global admin. Global admins with employee accounts see payroll subitems. */
  isGlobalAdmin?: boolean;
  /** Whether the user has the employer role. */
  hasEmployerRole: boolean;
  /** Whether the user can view their own payslip documents. */
  canViewSelfPayslips?: boolean;
  /** Whether the user can access payroll to run in the sidebar. */
  canViewPayrollToRun?: boolean;
  /** Whether to show the notifications menu item. Default true. */
  showNotifications?: boolean;
  /** Whether to show the standard sidebar footer items; help desk remains visible. Default true. */
  showFooterMenuItems?: boolean;
  /** Whether to show help desk in the sidebar footer. Default true. */
  showHelpDeskInSidebar?: boolean;
  /** Desktop notifications slot. */
  slotNotifications: ReactNode;
  /** Mobile notifications – receives open state from AppNavigation. */
  renderNotificationsMobile: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  /** Mobile organisation switcher subview for the profile menu. */
  renderOrganisationSwitcherMobile?: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  /** Logout button/content. */
  slotLogout: ReactNode;
  /** Mobile profile button initials. */
  mobileProfileInitials?: string;
  /** Mobile profile menu items rendered from the right-side initials button. */
  mobileProfileItems?: MenuItem[];

  /** Base URL for the EOR app. */
  eorBase?: string;
  /** Base URL for the Wallet app. */
  walletBase?: string;
  /** Feature flags. */
  featureFlags: Record<string, boolean>;
  /** Whether the active organisation is a partner account. */
  isPartnerOrganisation?: boolean;
  /** Unread notification counts per category and subcategory for sidebar badges. */
  notificationCounts?: NavNotificationCounts;
  /** Wallet app sub-navigation items rendered under Wallet in the sidebar. */
  walletSubitems?: MenuItem['items'];
}

export function AppNavigation({
  app,
  isEmployer,
  isContractor,
  isGigWorker,
  isB2CStandard,
  isB2CFree,
  isB2CEmployee,
  isEmployed,
  isTeamLead = false,
  isGlobalAdmin = false,
  hasEmployerRole,
  canViewSelfPayslips = false,
  canViewPayrollToRun = false,
  showNotifications = true,
  showFooterMenuItems = true,
  showHelpDeskInSidebar = true,
  slotNotifications,
  renderNotificationsMobile,
  renderOrganisationSwitcherMobile,
  slotLogout,
  mobileProfileInitials,
  mobileProfileItems = [],
  eorBase,
  walletBase,
  featureFlags,
  isPartnerOrganisation = false,
  notificationCounts,
  walletSubitems,
}: AppNavigationProps) {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isOrganisationSwitcherOpen, setIsOrganisationSwitcherOpen] = useState(false);
  const isLargeTablet = useIsLargeTablet();

  const getLinkUrl = useCallback(
    (path: string, targetApp: AppNav): string => {
      if (app === targetApp) return path;
      const base = targetApp === APP_WALLET ? walletBase : eorBase;
      return path === '/' ? (base ?? '') : `${base ?? ''}${path}`;
    },
    [app, walletBase, eorBase]
  );

  const walletLink: MenuItem = useMemo(() => {
    const baseUrl = getLinkUrl('/', APP_WALLET);

    if (walletSubitems?.length) {
      const items =
        app === APP_WALLET
          ? walletSubitems
          : walletSubitems.map((sub) => ({
              ...sub,
              url: getLinkUrl(sub.url, APP_WALLET),
              isPathActive: undefined,
            }));

      return {
        title: 'Wallet',
        url: baseUrl,
        icon: <WalletCards />,
        items,
      };
    }

    return {
      title: 'Wallet',
      url: baseUrl,
      ...(app === APP_WALLET
        ? {
            urlPrefix: '/',
            excludeUrlPrefix: [
              '/expenses',
              '/client-invoicing',
              '/contractors/payments',
              '/payment-recipients',
            ],
          }
        : {}),
      icon: <WalletCards />,
    };
  }, [app, getLinkUrl, walletSubitems]);

  const clientInvoicingLink: MenuItem = useMemo(
    () => ({
      title: 'Client invoicing',
      url: getLinkUrl('/client-invoicing', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/client-invoicing' } : {}),
      icon: <FileText />,
    }),
    [app, getLinkUrl]
  );

  const expensesLink: MenuItem = useMemo(
    () => ({
      title: 'Expenses',
      url: getLinkUrl('/expenses', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/expenses' } : {}),
      icon: <ReceiptText />,
      category: NOTIFICATION_CATEGORIES.EXPENSES,
    }),
    [app, getLinkUrl]
  );

  const helpDeskLink: MenuItem = useMemo(
    () => ({
      title: 'Help desk',
      url: HELP_DESK_URL,
      icon: <CircleHelp />,
      openInNewTab: true,
    }),
    []
  );

  const contractorPaymentsLink: MenuItem = useMemo(
    () => ({
      title: isEmployer ? 'Contractor payments' : 'Payment inputs',
      url: getLinkUrl('/contractors/payments', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/contractors/payments' } : {}),
      icon: <FileClock />,
    }),
    [app, getLinkUrl, isEmployer]
  );

  const buildMenuItems = useCallback(
    (isMobile: boolean): MenuItem[] => {
      const footerItems = showFooterMenuItems
        ? buildFooterMenuItems({
            isMobile,
            getLinkUrl,
            helpDeskLink,
            showNotifications,
            isNotificationsOpen,
            setIsNotificationsOpen,
            renderNotificationsMobile,
            slotNotifications,
            slotLogout,
            showHelpDeskInSidebar,
          })
        : showHelpDeskInSidebar
          ? [
              {
                ...helpDeskLink,
                ...(isMobile ? { spaceAbove: true } : { bottom: true }),
              },
            ]
          : [];

      let mainItems: MenuItem[];

      if (isB2CFree) {
        mainItems = buildB2CStandardMainItems({
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else if (isEmployer) {
        mainItems = buildEmployerMainItems({
          app,
          getLinkUrl,
          hasEmployerRole,
          isGlobalAdmin,
          featureFlags,
          isPartnerOrganisation,
          canViewPayrollToRun,
          walletLink,
          expensesLink,
          contractorPaymentsLink,
        });
      } else if (isContractor) {
        mainItems = buildContractorMainItems({
          app,
          getLinkUrl,
          featureFlags,
          canViewPayrollToRun,
          walletLink,
          clientInvoicingLink,
          expensesLink,
          contractorPaymentsLink,
          hasEmployerRole,
          isGlobalAdmin,
          isTeamLead,
        });
      } else if (isGigWorker) {
        mainItems = buildGigWorkerMainItems({
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else if (isB2CEmployee) {
        mainItems = buildB2CEmployeeMainItems({
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else if (isB2CStandard) {
        mainItems = buildB2CStandardMainItems({
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else {
        mainItems = buildEmployeeMainItems({
          app,
          getLinkUrl,
          featureFlags,
          canViewPayrollToRun,
          walletLink,
          clientInvoicingLink,
          expensesLink,
          hasEmployerRole,
          isGlobalAdmin,
          isTeamLead,
          isEmployed,
        });
      }

      const payslipItems = canViewSelfPayslips ? [payslipsNavItem({ app, getLinkUrl })] : [];

      return applyNotificationBadgesToItems(
        [...mainItems, ...payslipItems, ...footerItems],
        notificationCounts
      );
    },
    [
      app,
      canViewPayrollToRun,
      canViewSelfPayslips,
      clientInvoicingLink,
      contractorPaymentsLink,
      expensesLink,
      featureFlags,
      getLinkUrl,
      isB2CEmployee,
      isB2CFree,
      isB2CStandard,
      isContractor,
      isEmployed,
      isEmployer,
      isGigWorker,
      isPartnerOrganisation,
      isGlobalAdmin,
      hasEmployerRole,
      isNotificationsOpen,
      isTeamLead,
      helpDeskLink,
      notificationCounts,
      renderNotificationsMobile,
      showNotifications,
      showFooterMenuItems,
      showHelpDeskInSidebar,
      slotLogout,
      slotNotifications,
      walletLink,
    ]
  );

  const menuItems = buildMenuItems(false);
  const mobileMenuItems = buildMenuItems(true);

  return (
    <>
      {!isLargeTablet && <AppSidebar menuItems={menuItems} />}
      {isLargeTablet && (
        <AppNavbar
          menuItems={mobileMenuItems}
          isNotificationsOpen={isNotificationsOpen}
          setIsNotificationsOpen={setIsNotificationsOpen}
          isOrganisationSwitcherOpen={isOrganisationSwitcherOpen}
          setIsOrganisationSwitcherOpen={setIsOrganisationSwitcherOpen}
          profileInitials={mobileProfileInitials}
          profileItems={[
            ...(renderOrganisationSwitcherMobile
              ? [
                  {
                    title: 'Organisation',
                    component: renderOrganisationSwitcherMobile({
                      isOpen: isOrganisationSwitcherOpen,
                      setIsOpen: setIsOrganisationSwitcherOpen,
                    }),
                    subviewKey: 'organisationSwitcher' as const,
                  },
                ]
              : []),
            {
              title: 'Notifications',
              component: renderNotificationsMobile({
                isOpen: isNotificationsOpen,
                setIsOpen: setIsNotificationsOpen,
              }),
              subviewKey: 'notifications' as const,
            },
            ...mobileProfileItems,
          ]}
        />
      )}
    </>
  );
}
